import React, { useCallback, useEffect, useState } from "react";
import api from "../../lib/api";
import { fmtPrice } from "../../lib/format";
import { toast } from "sonner";
import {
  Lock, RefreshCw, XCircle, Clock, Layers, ShieldAlert, TrendingUp, TrendingDown,
  Check, X, Target,
} from "lucide-react";

const STATUS_META = {
  DRAFT: { c: "#8a8a95", l: "Draft" },
  WAITING_FOR_ENTRY: { c: "#a78bfa", l: "Waiting for Entry" },
  ENTRY_ZONE_TOUCHED: { c: "#f59e0b", l: "Entry Zone Touched" },
  ACTIVE_RUNNING: { c: "#22c55e", l: "Active Running" },
  TP1_HIT: { c: "#22c55e", l: "TP1 Hit" },
  TP2_HIT: { c: "#16a34a", l: "TP2 Hit" },
  SUSPENDED: { c: "#22d3ee", l: "Suspended" },
  NEEDS_REVIEW: { c: "#f59e0b", l: "Needs Review" },
  COMPLETED: { c: "#22c55e", l: "Completed" },
  STOP_LOSS_HIT: { c: "#ef4444", l: "Stop Loss Hit" },
  INVALIDATED: { c: "#ef4444", l: "Invalidated" },
  MISSED_ENTRY: { c: "#f59e0b", l: "Missed Entry" },
  EXPIRED: { c: "#ef4444", l: "Expired" },
  CANCELLED: { c: "#8a8a95", l: "Cancelled" },
  DATA_FAILURE: { c: "#ef4444", l: "Data Failure" },
  QUEUED: { c: "#a78bfa", l: "Queued" },
};
const meta = (s) => STATUS_META[s] || { c: "#8a8a95", l: s || "—" };

const TIMELINE = ["WAITING_FOR_ENTRY", "ENTRY_ZONE_TOUCHED", "ACTIVE_RUNNING", "TP1_HIT", "TP2_HIT", "COMPLETED"];
const TL_LABEL = { WAITING_FOR_ENTRY: "Waiting", ENTRY_ZONE_TOUCHED: "Touched", ACTIVE_RUNNING: "Active", TP1_HIT: "TP1", TP2_HIT: "TP2", COMPLETED: "Done" };

const wita = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    const w = new Date(d.getTime() + 8 * 3600000 + d.getTimezoneOffset() * 60000);
    return w.toLocaleString("en-GB", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) + " WITA";
  } catch { return "—"; }
};
const duration = (fromIso, toIso) => {
  if (!fromIso) return "—";
  const a = new Date(fromIso).getTime();
  const b = toIso ? new Date(toIso).getTime() : Date.now();
  const mins = Math.max(0, Math.floor((b - a) / 60000));
  const d = Math.floor(mins / 1440), h = Math.floor((mins % 1440) / 60), m = mins % 60;
  return `${d ? d + "d " : ""}${h}h ${m}m`;
};

export default function SwingSetupCard({ symbol }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => { api.getSwing(symbol).then(setData).catch(() => {}); }, [symbol]);
  useEffect(() => { load(); const t = setInterval(load, 5000); return () => clearInterval(t); }, [load]);

  const generate = async () => {
    setBusy(true);
    try {
      const r = await api.generateSwing(symbol);
      toast.success(r.published ? `Setup published: ${r.setup.planId}` : "Saved as QUEUED candidate — active setup stays locked");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not generate setup"); }
    finally { setBusy(false); }
  };
  const cancel = async (pid) => {
    setBusy(true);
    try { await api.cancelSwing(pid, "Manually cancelled by user"); toast.success("Setup cancelled"); load(); }
    catch { toast.error("Could not cancel"); } finally { setBusy(false); }
  };

  const s = data?.active || (data?.archived && data.archived[0]);
  const candidate = data?.candidate;
  const terminal = s && ["COMPLETED", "STOP_LOSS_HIT", "INVALIDATED", "MISSED_ENTRY", "EXPIRED", "CANCELLED", "DATA_FAILURE"].includes(s.status);

  return (
    <div className="ym-panel p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Layers size={15} style={{ color: "var(--accent-soft)" }} />
          <h3 className="text-[13px] font-bold">Status Setup Swing</h3>
          <span className="pill" style={{ fontSize: 9, color: "#fff", background: "rgba(139,92,246,.25)", borderColor: "rgba(139,92,246,.4)" }}>4H</span>
        </div>
        <button onClick={generate} disabled={busy} className="flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-[11px] font-semibold ym-hover"
          style={{ background: "linear-gradient(135deg,#8b5cf6,#7c3aed)", color: "#fff" }}>
          <RefreshCw size={12} className={busy ? "animate-spin" : ""} /> Buat Setup
        </button>
      </div>

      {!s ? (
        <div className="rounded-xl p-6 text-center text-[12px]" style={{ background: "var(--panel-2)", border: "1px dashed var(--border)", color: "var(--text-muted)" }}>
          Tidak ada setup swing aktif untuk {symbol}. Klik Buat Setup untuk membuat dari candle 4H tertutup terakhir.
        </div>
      ) : (
        <>
          {/* header row */}
          <div className="flex flex-wrap items-center gap-2 mb-3">
            <span className="text-[16px] font-extrabold" style={{ color: s.direction === "LONG" ? "var(--green)" : "var(--red)" }}>
              {s.direction === "LONG" ? <TrendingUp size={16} className="inline mb-0.5" /> : <TrendingDown size={16} className="inline mb-0.5" />} {s.direction}
            </span>
            <span className="pill" style={{ color: meta(s.status).c, background: "var(--panel-2)", borderColor: "var(--border)" }}>{meta(s.status).l}</span>
            <span className="pill" style={{ color: "var(--amber)", background: "rgba(245,158,11,.1)", borderColor: "rgba(245,158,11,.3)" }}><Lock size={11} /> RENCANA TERKUNCI</span>
            <span className="ml-auto text-[10px] tabnum" style={{ color: "var(--text-dim)" }}>{s.planId} · v{s.version}</span>
          </div>

          <div className="text-[11px] rounded-lg p-2 mb-3" style={{ background: "rgba(139,92,246,.06)", border: "1px solid rgba(139,92,246,.2)", color: "var(--text-muted)" }}>
            Rencana ini terkunci. Data pasar live memperbarui statusnya tetapi tidak menghitung ulang levelnya.
          </div>

          {/* progress summary */}
          {s.progress && <ProgressBlock s={s} />}

          {/* trigger + invalidation */}
          <div className="text-[10.5px] space-y-1.5 mb-3">
            <Note icon={Clock} label="Entry Trigger" text={s.entryTrigger} />
            <Note icon={ShieldAlert} label="Invalidasi" text={s.invalidationRule} color="var(--red)" />
          </div>

          {/* score */}
          <div className="rounded-lg p-2.5 mb-3" style={{ background: "var(--panel-2)", border: "1px solid var(--border)" }}>
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-[11px] font-semibold">Skor Setup</span>
              <span className="text-[13px] font-extrabold tabnum" style={{ color: "var(--accent-soft)" }}>{s.score.total}<span className="text-[10px]" style={{ color: "var(--text-dim)" }}>/{s.score.max}</span></span>
            </div>
            {s.score.components.map((c) => (
              <div key={c.key} className="flex items-center gap-2 text-[10px] mb-0.5">
                <span className="capitalize w-20" style={{ color: "var(--text-muted)" }}>{c.key}</span>
                <div className="flex-1 h-1 rounded-full overflow-hidden" style={{ background: "#0004" }}>
                  <div className="h-full" style={{ width: `${(c.points / c.weight) * 100}%`, background: c.points === 0 ? "var(--amber)" : "var(--accent-soft)" }} />
                </div>
                <span className="tabnum w-10 text-right" style={{ color: "var(--text-dim)" }}>{c.points}/{c.weight}</span>
              </div>
            ))}
            <div className="text-[9px] mt-1" style={{ color: "var(--amber)" }}>Makro & Geopolitik masih DEMO — tidak termasuk skor live.</div>
          </div>

          {/* timing */}
          <div className="grid grid-cols-2 gap-2 text-[10px] mb-3">
            <TimeBox label="Dibuat" v={wita(s.createdAt)} />
            <TimeBox label="Source 4H Close" v={wita(s.sourceCandleClose)} />
            <TimeBox label="Berlaku Sampai" v={wita(s.validUntil)} />
            <TimeBox label="Running" v={duration(s.activatedAt, s.closedAt)} />
          </div>

          <button onClick={() => cancel(s.planId)} disabled={busy || terminal}
            className="w-full flex items-center justify-center gap-1.5 rounded-lg py-2 text-[11px] font-semibold ym-hover"
            style={{ background: "var(--panel-2)", border: "1px solid var(--border)", color: terminal ? "var(--text-dim)" : "var(--red)" }}>
            <XCircle size={13} /> {terminal ? `Closed · ${meta(s.status).l}` : "Batalkan Setup"}
          </button>
        </>
      )}

      {/* candidate */}
      {candidate && (
        <div className="mt-3 rounded-lg p-2.5" style={{ background: "rgba(167,139,250,.08)", border: "1px solid rgba(167,139,250,.3)" }}>
          <div className="flex items-center gap-2 mb-1">
            <span className="pill" style={{ color: "var(--accent-soft)", background: "rgba(139,92,246,.16)", borderColor: "transparent" }}>QUEUED CANDIDATE</span>
            <span className="text-[11px] font-bold" style={{ color: candidate.direction === "LONG" ? "var(--green)" : "var(--red)" }}>{candidate.direction}</span>
            <span className="ml-auto text-[10px] tabnum" style={{ color: "var(--text-dim)" }}>score {candidate.score.total}</span>
          </div>
          <div className="text-[10px]" style={{ color: "var(--text-muted)" }}>{candidate.queueReason}</div>
          <div className="text-[10px] mt-1 font-semibold" style={{ color: "var(--accent-soft)" }}>Current active setup remains locked.</div>
        </div>
      )}
    </div>
  );
}

function ProgressBlock({ s }) {
  const p = s.progress;
  const pct = p.percent || 0;
  const entry = p.entryState;
  const entryMeta = {
    ENTRY_PENDING: { c: "#8b5cf6", l: "ENTRY PENDING", fill: false },
    ENTRY_TOUCHED: { c: "#f59e0b", l: "ENTRY TOUCHED", fill: false },
    ENTRY_ACTIVE: { c: "#8b5cf6", l: "ENTRY ACTIVE", fill: true },
    VERIFICATION_REQUIRED: { c: "#f59e0b", l: "VERIFICATION REQUIRED", fill: false },
    ENTRY_STOPPED: { c: "#ef4444", l: "STOPPED OUT", fill: false },
    ENTRY_INVALIDATED: { c: "#ef4444", l: "INVALIDATED", fill: false },
    ENTRY_COMPLETED: { c: "#22c55e", l: "COMPLETED", fill: false },
  }[entry] || { c: "#8b5cf6", l: entry, fill: false };
  const slHit = p.slState === "SL_HIT";

  return (
    <div className="mb-3">
      <div className="rounded-lg p-2.5 mb-2" style={{ background: "var(--panel-2)", border: "1px solid var(--border)" }}>
        <div className="flex items-center justify-between mb-1.5">
          <span className="text-[10px] font-bold tracking-wide" style={{ color: "var(--text-dim)" }}>TRADE PLAN PROGRESS</span>
          <span className="text-[11px] font-bold" style={{ color: "var(--green)" }}>{p.summaryLabel} · {pct}%</span>
        </div>
        <div className="h-2 rounded-full overflow-hidden mb-1.5" style={{ background: "#0006" }}>
          <div className="h-full rounded-full" style={{ width: `${pct}%`, background: "linear-gradient(90deg,#16a34a,#22c55e)", transition: "width .5s ease" }} />
        </div>
        {p.nextTarget ? (
          <div className="text-[10px]" style={{ color: "var(--text-muted)" }}>
            Next: <b style={{ color: "var(--green)" }}>TP{p.nextTarget.tp} at {fmtPrice(p.nextTarget.price)}</b> · {fmtPrice(p.nextTarget.distance)} / {p.nextTarget.distancePct}% away
          </div>
        ) : (
          <div className="text-[10px]" style={{ color: "var(--text-dim)" }}>{entry === "ENTRY_ACTIVE" ? "All targets resolved" : "Menunggu aktivasi entry"}</div>
        )}
      </div>

      <div className="flex items-center gap-1 mb-2 overflow-x-auto pb-1">
        {p.timeline.map((t, i) => {
          const done = t.state === "HIT";
          const touched = t.state === "TOUCHED";
          const col = done ? "var(--green)" : touched ? "var(--amber)" : "var(--border)";
          return (
            <React.Fragment key={t.label}>
              <div className="flex flex-col items-center shrink-0" style={{ minWidth: 40 }}>
                <div className="grid place-items-center" style={{ width: 14, height: 14, borderRadius: 999, background: done ? "var(--green)" : "transparent", border: `1.5px solid ${col}` }}>
                  {done && <Check size={9} color="#04120a" strokeWidth={3.5} />}
                </div>
                <span className="text-[7.5px] mt-1 font-semibold" style={{ color: done ? "var(--text)" : "var(--text-dim)" }}>{t.label}</span>
              </div>
              {i < p.timeline.length - 1 && <div className="h-px flex-1 min-w-[10px]" style={{ background: done ? "var(--green)" : "var(--border)" }} />}
            </React.Fragment>
          );
        })}
      </div>

      <div className="grid grid-cols-2 gap-2 mb-2">
        <div className="rounded-lg p-2.5" style={{ background: entryMeta.fill ? "linear-gradient(135deg,rgba(139,92,246,.35),rgba(124,58,237,.22))" : "var(--panel-2)", border: `1px solid ${entryMeta.fill ? "rgba(139,92,246,.6)" : "var(--border)"}` }}>
          <div className="flex items-center gap-1.5 text-[10px] font-bold" style={{ color: entryMeta.fill ? "#fff" : entryMeta.c }}>
            {entryMeta.fill && <Check size={12} />}{entryMeta.l}
          </div>
          <div className="text-[11px] tabnum font-bold mt-0.5">{fmtPrice(s.entryLow)}–{fmtPrice(s.entryHigh)}</div>
          {entry === "ENTRY_ACTIVE" && (
            <div className="text-[8.5px] mt-0.5" style={{ color: entryMeta.fill ? "rgba(255,255,255,.75)" : "var(--text-dim)" }}>
              @ {fmtPrice(p.entryPrice)} · {wita(p.entryActivatedAt)}
            </div>
          )}
        </div>
        <div className="rounded-lg p-2.5" style={{ background: slHit ? "linear-gradient(135deg,rgba(239,68,68,.4),rgba(185,28,28,.25))" : "var(--panel-2)", border: `1px solid ${slHit ? "rgba(239,68,68,.6)" : "var(--border)"}` }}>
          <div className="flex items-center gap-1.5 text-[10px] font-bold" style={{ color: slHit ? "#fff" : "#ef4444" }}>
            {slHit ? <X size={12} /> : <span style={{ width: 7, height: 7, borderRadius: 2, background: "#ef4444", display: "inline-block" }} />}{slHit ? "STOP LOSS HIT" : "STOP LOSS"}
          </div>
          <div className="text-[11px] tabnum font-bold mt-0.5">{fmtPrice(s.stopLoss)}</div>
          {slHit && <div className="text-[8.5px] mt-0.5" style={{ color: "rgba(255,255,255,.75)" }}>@ {fmtPrice(p.slHitPrice)} · {wita(p.slHitAt)}</div>}
        </div>
      </div>

      <div className="grid grid-cols-3 gap-2">
        {p.targets.map((t) => <TargetCard key={t.tp} t={t} />)}
      </div>
    </div>
  );
}

function TargetCard({ t }) {
  const hit = t.state === "HIT";
  const next = t.state === "NEXT";
  return (
    <div className="rounded-lg p-2" style={{
      background: hit ? "linear-gradient(135deg,rgba(34,197,94,.42),rgba(22,163,74,.24))" : "var(--panel-2)",
      border: `1px solid ${hit ? "rgba(34,197,94,.65)" : next ? "rgba(34,197,94,.5)" : "var(--border)"}`,
      boxShadow: next ? "0 0 0 1px rgba(34,197,94,.25), 0 6px 18px -10px rgba(34,197,94,.5)" : "none",
    }}>
      <div className="flex items-center gap-1 text-[9px] font-bold" style={{ color: hit ? "#fff" : next ? "var(--green)" : "var(--text-dim)" }}>
        {hit ? <Check size={11} /> : <Target size={10} style={{ color: next ? "var(--green)" : "#22c55e88" }} />}
        {hit ? `TP${t.tp} HIT` : next ? "NEXT TARGET" : "PENDING"}
      </div>
      <div className="text-[11px] tabnum font-bold mt-0.5" style={{ color: hit ? "#fff" : "var(--text)" }}>{fmtPrice(t.target)}</div>
      {hit ? (
        <div className="text-[8px] mt-0.5" style={{ color: "rgba(255,255,255,.8)" }}>
          hit {fmtPrice(t.hitPrice)}<br />{wita(t.hitAt)}
        </div>
      ) : next && t.distance != null ? (
        <div className="text-[8px] mt-0.5" style={{ color: "var(--text-dim)" }}>{t.distancePct}% away</div>
      ) : (
        <div className="text-[8px] mt-0.5" style={{ color: "var(--text-dim)" }}>&nbsp;</div>
      )}
    </div>
  );
}


function Row({ k, v, c }) {
  return (
    <div className="flex items-center justify-between rounded-lg px-3 py-1.5" style={{ background: "var(--panel-2)", border: "1px solid var(--border)" }}>
      <span className="flex items-center gap-2 text-[11px]" style={{ color: "var(--text-muted)" }}><span style={{ width: 7, height: 7, borderRadius: 2, background: c }} />{k}</span>
      <span className="tabnum text-[12px] font-bold">{v}</span>
    </div>
  );
}
function Note({ icon: Icon, label, text, color }) {
  return (
    <div className="flex items-start gap-1.5" style={{ color: "var(--text-muted)" }}>
      <Icon size={12} style={{ color: color || "var(--accent-soft)", marginTop: 1 }} />
      <span><b style={{ color: color || "var(--text)" }}>{label}:</b> {text}</span>
    </div>
  );
}
function TimeBox({ label, v }) {
  return (
    <div className="rounded-lg px-2.5 py-1.5" style={{ background: "var(--panel-2)", border: "1px solid var(--border)" }}>
      <div className="text-[8px] font-semibold tracking-wide" style={{ color: "var(--text-dim)" }}>{label.toUpperCase()}</div>
      <div className="text-[10px] font-semibold tabnum">{v}</div>
    </div>
  );
}
